import type { Card, Lane, Comment, Activity, BoardSnapshot, ChatMessage, Category, DailyOverview, WeeklyDigest, UserSettings, Attachment, Board, BoardContent } from "../types";
import { supabase, getAccessToken } from "./supabase";

const BASE = (import.meta.env.VITE_API_URL as string | undefined) || "";

type ApiErrorKind = "no_groq_key" | "unauthorized" | "rate_limited" | "server" | "network";

export class ApiError extends Error {
  kind: ApiErrorKind;
  status: number;
  body: any;
  constructor(kind: ApiErrorKind, status: number, message: string, body?: any) {
    super(message);
    this.kind = kind;
    this.status = status;
    this.body = body;
  }
}

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const token = await getAccessToken();
  const headers = new Headers(init?.headers || {});
  if (!headers.has("Content-Type") && !(init?.body instanceof FormData)) {
    headers.set("Content-Type", "application/json");
  }
  if (token) headers.set("Authorization", `Bearer ${token}`);

  let r: Response;
  try {
    r = await fetch(BASE + path, { ...init, headers });
  } catch (e: any) {
    throw new ApiError("network", 0, "sem conexão");
  }

  if (r.ok) {
    try {
      return await r.json();
    } catch {
      return undefined as any;
    }
  }

  let body: any = null;
  try {
    body = await r.json();
  } catch {
    try {
      body = { detail: await r.text() };
    } catch {}
  }

  if (r.status === 401) {
    // sessão expirada; força signout local
    await supabase.auth.signOut().catch(() => {});
    throw new ApiError("unauthorized", r.status, "não autenticado", body);
  }
  if (r.status === 412 && body?.detail?.code === "NO_GROQ_KEY") {
    throw new ApiError("no_groq_key", r.status, body.detail.detail || "configure sua chave Groq", body);
  }
  if (r.status === 429) {
    throw new ApiError("rate_limited", r.status, "Muitas requisições. Aguarde um momento.", body);
  }
  throw new ApiError("server", r.status, (typeof body?.detail === "string" ? body.detail : `erro ${r.status}`), body);
}

export const api = {
  getBoard: () => req<BoardSnapshot>("/api/board"),
  stats: () => req<any>("/api/stats"),
  health: () => req<any>("/api/health"),

  getSettings: () => req<UserSettings>("/api/settings"),
  updateSettings: (patch: {
    nickname?: string | null;
    tutorialCompleted?: boolean;
    dismissIagoBalloon?: boolean;
    weightLaneIds?: number[];
  }) =>
    req<UserSettings>("/api/settings", {
      method: "PATCH",
      body: JSON.stringify(patch),
    }),
  putGroqKey: (key: string) =>
    req<{ hasGroqKey: boolean; groqLast4: string }>("/api/settings/groq-key", {
      method: "PUT",
      body: JSON.stringify({ key }),
    }),
  deleteGroqKey: () =>
    req<{ hasGroqKey: boolean; groqLast4: string | null }>("/api/settings/groq-key", { method: "DELETE" }),
  testGroqKey: (key: string) =>
    req<{ ok: boolean }>("/api/settings/groq-key/test", {
      method: "POST",
      body: JSON.stringify({ key }),
    }),

  getBoards: (includeArchived = false) =>
    req<Board[]>(`/api/boards${includeArchived ? "?include_archived=true" : ""}`),
  getBoardContent: (id: number) =>
    req<BoardContent>(`/api/boards/${id}`),
  createBoard: (body: { title: string; icon?: string | null; color?: string | null }) =>
    req<{ board: Board; lanes: Lane[]; cards: Card[] }>("/api/boards", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  updateBoard: (id: number, body: Partial<Pick<Board, "title" | "icon" | "color" | "position" | "archived">>) =>
    req<Board>(`/api/boards/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  archiveBoard: (id: number) =>
    req<{ ok: boolean }>(`/api/boards/${id}`, { method: "DELETE" }),

  createLane: (body: { title: string; color?: string; isDone?: boolean; wipLimit?: number | null; boardId?: number }) =>
    req<Lane>("/api/lanes", { method: "POST", body: JSON.stringify(body) }),
  updateLane: (id: number, body: Partial<Lane>) =>
    req<Lane>(`/api/lanes/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  deleteLane: (id: number) => req<{ ok: boolean }>(`/api/lanes/${id}`, { method: "DELETE" }),

  createCard: (body: Partial<Card>) =>
    req<Card>("/api/cards", { method: "POST", body: JSON.stringify(body) }),
  updateCard: (id: number, body: Partial<Card>) =>
    req<Card>(`/api/cards/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  moveCard: (id: number, laneId: number, position: number) =>
    req<Card>(`/api/cards/${id}/move`, { method: "POST", body: JSON.stringify({ laneId, position }) }),
  reorderLane: (laneId: number, cardIds: number[]) =>
    req<{ ok: boolean }>(`/api/lanes/${laneId}/reorder`, {
      method: "POST",
      body: JSON.stringify({ laneId, cardIds }),
    }),
  deleteCard: (id: number) => req<{ ok: boolean }>(`/api/cards/${id}`, { method: "DELETE" }),
  duplicateCard: (id: number) => req<Card>(`/api/cards/${id}/duplicate`, { method: "POST" }),

  listComments: (cardId: number) => req<Comment[]>(`/api/cards/${cardId}/comments`),
  addComment: (cardId: number, body: string) =>
    req<Comment>(`/api/cards/${cardId}/comments`, { method: "POST", body: JSON.stringify({ body }) }),

  activity: (cardId: number) => req<Activity[]>(`/api/cards/${cardId}/activity`),

  transcribe: async (blob: Blob): Promise<{ text: string }> => {
    const token = await getAccessToken();
    const fd = new FormData();
    fd.append("file", blob, "audio.webm");
    const headers: Record<string, string> = {};
    if (token) headers.Authorization = `Bearer ${token}`;
    const r = await fetch(BASE + "/api/ai/transcribe", { method: "POST", body: fd, headers });
    if (!r.ok) {
      let body: any = null;
      try { body = await r.json(); } catch {}
      if (r.status === 412 && body?.detail?.code === "NO_GROQ_KEY") {
        throw new ApiError("no_groq_key", 412, body.detail.detail);
      }
      throw new ApiError("server", r.status, (typeof body?.detail === "string" ? body.detail : `erro ${r.status}`));
    }
    return r.json();
  },

  generateCard: (transcript: string, laneId?: number | null) =>
    req<{ card: Card; provider: string }>("/api/ai/generate-card", {
      method: "POST",
      body: JSON.stringify({ transcript, laneId }),
    }),

  refineCard: (cardId: number, instruction: string) =>
    req<Card>(`/api/ai/refine/${cardId}`, {
      method: "POST",
      body: JSON.stringify({ instruction }),
    }),

  listCategories: () => req<Category[]>("/api/categories"),
  createCategory: (body: { name: string; color?: string; kind?: string; icon?: string; aiProposed?: boolean }) =>
    req<Category>("/api/categories", { method: "POST", body: JSON.stringify(body) }),
  updateCategory: (id: number, body: Partial<Category>) =>
    req<Category>(`/api/categories/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  deleteCategory: (id: number) =>
    req<{ ok: boolean }>(`/api/categories/${id}`, { method: "DELETE" }),

  proactiveDaily: () => req<DailyOverview>("/api/proactive/daily"),
  proactiveWeekly: () => req<WeeklyDigest>("/api/proactive/weekly-digest"),

  // LGPD
  getMe: () => req<any>("/api/me"),
  exportMyData: () => req<any>("/api/me/export"),
  deleteMyAccount: () => req<{ ok: boolean; detail: string }>("/api/me", { method: "DELETE" }),

  chatHistory: () => req<ChatMessage[]>("/api/chat/history"),
  chatClear: () => req<{ ok: boolean }>("/api/chat/history", { method: "DELETE" }),
  chatMessage: (content: string, boardId?: number | null) =>
    req<{ user: ChatMessage; assistant: ChatMessage }>("/api/chat/message", {
      method: "POST",
      body: JSON.stringify({ content, ...(boardId != null ? { boardId } : {}) }),
    }),
  chatExecute: (messageId: number, actionIndex: number) =>
    req<{ message: ChatMessage; result: any }>("/api/chat/execute-action", {
      method: "POST",
      body: JSON.stringify({ messageId, actionIndex }),
    }),

  // attachments
  uploadAttachment: async (cardId: number, file: File): Promise<Attachment> => {
    const token = await getAccessToken();
    const fd = new FormData();
    fd.append("file", file);
    const headers: Record<string, string> = {};
    if (token) headers.Authorization = `Bearer ${token}`;
    const r = await fetch(BASE + `/api/cards/${cardId}/attachments`, { method: "POST", body: fd, headers });
    if (!r.ok) {
      let body: any = null;
      try { body = await r.json(); } catch {}
      if (r.status === 413) throw new ApiError("server", 413, "Arquivo excede 10 MB");
      throw new ApiError("server", r.status, (typeof body?.detail === "string" ? body.detail : `erro ${r.status}`));
    }
    return r.json();
  },
  getAttachmentUrl: (attachmentId: string) => `${BASE}/api/attachments/${attachmentId}`,
  deleteAttachment: (attachmentId: string) =>
    req<{ ok: boolean }>(`/api/attachments/${attachmentId}`, { method: "DELETE" }),

  // feedback (Espaço BETA)
  sendFeedback: (message: string, userEmail?: string) =>
    req<{ ok: boolean }>("/api/feedback", {
      method: "POST",
      body: JSON.stringify({ message, userEmail }),
    }),
};
